import store from '@/store';
import { formatDuration, formatDurationToMinutes, formatDistance, formatPrice, formatTimeFromTimestamp, formatDateFromTimestamp, capitalize, formatDistanceToKm, getDayName, calculateGasPrice, calculateGasConsumption } from '@/utils/format'
import { formatCountryInfo } from '@/utils/country-info-utils';
import { HAS_PUBLIC_TRANSPORT, VEHICLE_PUBLIC_COMBINED, NO_INTERMEDIATE_STOP_TRANSPORT_MODES } from '@/data/transportModesTypes';
import { PUBLIC_MEANS_OF_TRANSPORT_SUB } from '@/data/publicMeansOfTransport'
import { SHARING_MEANS_OF_TRANSPORT } from '@/data/sharingMeansOfTransport'
import routeSettingsDefaults from "@data/routeSettingsDefaults";
import groupBy from 'lodash/groupBy';

export function getBasicRouteData(route) {
    let r = {
        id: route.id,
        duration: formatDuration(route.metadata.duration),
        durationUnformatted: route.metadata.duration,
        fitBounds: route.fitBounds,
    }

    // distance
    if(!HAS_PUBLIC_TRANSPORT.includes(route.mode)) {
        r.distance = formatDistance(route.metadata.distance)
        r.distanceUnformatted= route.metadata.distance
    }

    // delay
    if(route.metadata.delays && route.metadata.delays.location) {
        r.delays = {
            total: 0
        }
        if(route.metadata.delays.location.arrival) {
            r.delays.arrival = formatDuration(route.metadata.delays.location.arrival.duration);
            r.delays.total += route.metadata.delays.location.arrival.duration
        }
    }

    // mode (for the LIST overview tab)
    if(route.mode) {
        r.mode = route.mode;
    }

    // duration color
    r.durationColor = 'black' // 'green';
    if(r.delays) {
        r.durationColor = 'orange';
    }

    if(route.mode === 'SHARING' && route.sharingVehicle) {
        const sharingModeData = SHARING_MEANS_OF_TRANSPORT.find(item => item.value === route.sharingVehicle)
        r.image = sharingModeData.image
    }

    return r
}

export function getListResultRouteData(route, options = {}) {
    let r = getBasicRouteData(route);

    // tolls
    if(route.metadata.tolls) {
        r.tolls = {}

        // vignette
        if(route.metadata.tolls.entries){
            const routeCountries = getCountryNamesFromCountryInfo(route)
            route.metadata.tolls.entries.forEach(toll => {
                const tollCountryCode = toll.countryCode
                let tollCountry = tollCountryCode
                // find the country name
                if(routeCountries && routeCountries.length > 0) {
                    const matchingCountry = routeCountries.find(country => country.countryCode === tollCountryCode)
                    if(matchingCountry) tollCountry = matchingCountry.name
                }

                // vignette
                if(toll.type === 'VignetteEntry') {
                    if(!r.tolls.vignette) r.tolls.vignette = []
                    if(!r.tolls.vignette.find(entry => entry.country === tollCountry)) {
                        const entry = {
                            country: tollCountry,
                            countryCode: tollCountryCode
                        }
                        if(toll.vignetteDistance) {
                            entry.distance = formatDistanceToKm(toll.vignetteDistance, true, true)
                        }
                        r.tolls.vignette.push(entry)
                    }
                }
                // toll
                if(toll.type === 'TollEntry') {
                    if(!r.tolls.toll) r.tolls.toll = []
                    if(!r.tolls.toll.find(entry => entry.country === tollCountry) && tollCountryCode) {
                        const entry = {
                            country: tollCountry,
                            countryCode: tollCountryCode
                        }
                        if(toll.amountEuro) {
                            entry.amountEuro = formatPrice(toll.amountEuro)
                        }
                        if(toll.amountCountry && toll.currencyCountry) {
                            entry.amountCountry = `${toll.currencyCountry} ${toll.amountCountry}`
                        }
                        r.tolls.toll.push(entry)
                    }
                }
            })
        }

        // toll sum
        if(route.metadata.tolls.tollSumEuro){
            r.tolls.tollSumEuro = formatPrice(route.metadata.tolls.tollSumEuro);
        }
    }

    // green zones
    if(route.metadata.greenzones && route.metadata.greenzones.length > 0) {
        const greenzones = store.getters.routeGreenzones[route.mode]
        r.greenzones = []
        route.metadata.greenzones.forEach(zoneId => {
            const fullZone = greenzones.find(zone => zone.id === zoneId)
            if(fullZone) r.greenzones.push(fullZone)
        })
        r.greenzones = groupBy(r.greenzones, 'country')
    }

    // route hints
    if(route.metadata.routingHints?.hints && route.metadata.routingHints?.hints?.length > 0) {
        const routeCountries = getCountryNamesFromCountryInfo(route)
        r.routingHints = []
        route.metadata.routingHints?.hints.forEach(hint => {
            if(hint.entries && hint.entries.length > 0) {
                const matchingCountry = routeCountries.find(country => country.countryCode === hint.countryCode)
                const countryName = matchingCountry ? matchingCountry.name : hint.countryCode
                const hintData = {
                    title: `Routenhinweis ${countryName}`,
                    text: hint.entries.map(entry => `${entry.roadNames.join('/')}: ${entry.text}`).join('<br><br>')
                }
                r.routingHints.push(hintData)
            }
        })
    }

    // add polylines
    if(route.sections) {
        let polylines = [];
        const sectionCount = route.sections.length;
        let previousSectionRide = false;
        route.sections.forEach((section, sectionIndex) => {
            if(section.polylineWKT){
                polylines.push({
                    polyline: section.polylineWKT,
                    lineColor: section.lineColor ? section.lineColor : '#929292',
                    dotted: isSectionDotted(section, route),
                    markInterimStart: section.type === 'Ride' && sectionIndex !== 0 && !previousSectionRide,
                    markInterimEnd: section.type === 'Ride' && sectionIndex !== sectionCount - 1,
                });
                previousSectionRide = section.type === 'Ride';
            }
        })
        r.polylines = polylines;
    }

    // sections - for public transport steps display
    if(route.sections && route.sections.length > 1 && HAS_PUBLIC_TRANSPORT.includes(route.mode)) {
        r.sections = [];
        r.hasCancellation = false
        r.hasUnreachableConnection = false
        const firstRide = route.sections.find(ride => ride.type === 'Ride'); //get first ride
        const reverseRoute= [...route.sections]; 
        let lastRide = reverseRoute.reverse().find(ride => ride.type === 'Ride'); // get last ride
        
        //calculate delay for departure
        if(firstRide.departure.time.realTime) {
            let differenceDeparture = new Date(firstRide.departure.time.realTime) - new Date(firstRide.departure.time.plannedTime);
            let realTimeDeparture = Math.floor((differenceDeparture/1000/60) << 0);
            r.delayDeparture = realTimeDeparture;
        }
        
        //calculate delay for arrival
        if(lastRide.arrival.time.realTime) {
            let differenceArrival = new Date(lastRide.arrival.time.realTime) - new Date(lastRide.arrival.time.plannedTime);
            let realTimeArrival = Math.floor((differenceArrival/1000/60) << 0);
            r.delayArrival = realTimeArrival;
        }

        route.sections.forEach((apiSection, apiSectionIndex) => {
            let section = {type: apiSection.type};
            const previousSection = apiSectionIndex && route.sections[apiSectionIndex - 1] ? route.sections[apiSectionIndex - 1] : false
            if(apiSection.lineColor) {
                section.color = apiSection.lineColor
            }
            if(section.type === 'Ride' && apiSection.line) {
                if(apiSection.line.iconText){
                    section.name = apiSection.line.iconText;
                }
                if(apiSection.line.type){
                    section.lineType = apiSection.line.type;
                }

                //temporary solution for section colors
                section.color = apiSection.lineColor ?? '#929292';

                if(options.detailedSections) {
                    if(apiSection.line.destination){
                        section.destination = apiSection.line.destination;
                    }
                    if(apiSection.line.additional){
                        section.lineInformation = apiSection.line.additional;
                    }
                }
            }
            if(options.detailedSections) {
                section.stations = apiSection.stations;
                section.departureTime = false;
                section.realDepartureTime = false;
                section.delayDeparture = false

                if(apiSection.departure && apiSection.departure.time) {
                    section.departureTime = getTimeFromTimestamp(apiSection.departure.time.plannedTime)
                    if(apiSection.departure.time.realTime) {
                        const timeDifference = new Date(apiSection.departure.time.realTime) - new Date(apiSection.departure.time.plannedTime);
                        const timeDifferenceMinutes = Math.floor((timeDifference/1000/60) << 0);
                        section.realDepartureTime = getTimeFromTimestamp(apiSection.departure.time.realTime)
                        section.delayDeparture = timeDifferenceMinutes
                        section.delayDepartureFormatted = `${timeDifferenceMinutes < 0 ? '-' : '+'} ${timeDifferenceMinutes < 0 ? timeDifferenceMinutes * -1 : timeDifferenceMinutes}`
                    }
                }
                section.departureStation = apiSection.departure && apiSection.departure.station ? apiSection.departure.station.name : false;
                section.departurePlatform = false
                if(apiSection.departure && apiSection.departure.station && (apiSection.departure.station.platform || apiSection.departure.station.plannedPlatform)) {
                    section.departurePlatform = {
                        platform: apiSection.departure.station.platform || apiSection.departure.station.plannedPlatform,
                        platformChanged: apiSection.departure.station.platform && apiSection.departure.station.plannedPlatform && apiSection.departure.station.platform !== apiSection.departure.station.plannedPlatform
                    }
                }
                // section.arrivalPlatform = apiSectionIndex !== 0 && apiSection.arrival && apiSection.arrival.station ? apiSection.arrival.station.plannedPlatform : false;
                section.dotted = isSectionDotted(apiSection, route);
                section.messages = apiSection.operationMessages && apiSection.operationMessages.messages ? apiSection.operationMessages.messages : false;
                if(section.messages && !r.sectionsHaveMessages) {
                    r.sectionsHaveMessages = true;
                }
                section.distance = formatDistance(apiSection.distance);
                section.duration = formatDuration(apiSection.duration);
            }
            // the arrival platform of the previous section is the correct one
            if(previousSection && previousSection.arrival && previousSection.arrival.station && (previousSection.arrival.station.platform || previousSection.arrival.station.plannedPlatform)) {
                section.arrivalPlatform = {
                    platform: previousSection.arrival.station.platform || previousSection.arrival.station.plannedPlatform,
                    platformChanged: previousSection.arrival.station.platform && previousSection.arrival.station.plannedPlatform && previousSection.arrival.station.platform !== previousSection.arrival.station.plannedPlatform
                }
            }
            // check if previous ride has a delay on the arrival
            if(section.type === 'Walk' && apiSectionIndex && r.sections[apiSectionIndex - 1]) {
                if(previousSection && previousSection.type === 'Ride' && previousSection.arrival && previousSection.arrival.time && previousSection.arrival.time.realTime) {
                    const timeDifference = new Date(previousSection.arrival.time.realTime) - new Date(previousSection.arrival.time.plannedTime);
                    const timeDifferenceMinutes = Math.floor((timeDifference/1000/60) << 0);
                    section.realDepartureTime = getTimeFromTimestamp(previousSection.arrival.time.realTime)
                    section.delayDeparture = timeDifferenceMinutes
                    section.delayDepartureFormatted = `${timeDifferenceMinutes < 0 ? '-' : '+'} ${timeDifferenceMinutes < 0 ? timeDifferenceMinutes * -1 : timeDifferenceMinutes}`
                }
            }
            if(section.type === 'Walk' && apiSectionIndex === 0 && store.getters.routeWaypoints.length) {
                const label = store.getters.routeWaypoints[0].label;
                section.departureStation = label.includes(',') ? label.split(',')[0] : label;
            }
            if(section.type === 'Drive') {
                section.steps = apiSection.turns
            }
            // park / bike and ride use both the same section.type 'Drive'
            if(section.type === 'Drive' && route.mode === 'BIKE_AND_RIDE') {
                section.type = 'Bike'
            }

            // check if any ride section is cancelled
            if(section.type === 'Ride' && ((apiSection.departure.station.cancellation && apiSection.departure.station.cancellation.isCancelled) || (apiSection.arrival.station.cancellation && apiSection.arrival.station.cancellation.isCancelled))) {
                r.hasCancellation = true

                if(options.detailedSections) {
                    section.isCancelled = true
                    section.departureCancelled = apiSection.departure.station.cancellation && apiSection.departure.station.cancellation.isCancelled
                    section.arrivalCancelled = apiSection.arrival.station.cancellation && apiSection.arrival.station.cancellation.isCancelled
                }
            }
            // check if previous section has a cancellation
            if(r.sections[apiSectionIndex - 1] && r.sections[apiSectionIndex - 1].arrivalCancelled) {
                if(options.detailedSections) {
                    section.isCancelled = true
                }
            }

            // check if any section is not reachable
            if(section.type === 'Ride' && apiSection.reachable === false) {
                r.hasUnreachableConnection = true
            }

            // show details of the walk
            if(section.type === 'Walk' && apiSection.duration) {
                section.minuteDuration = formatDurationToMinutes(apiSection.duration)
                section.driveLabel = 'Fußweg'
                section.steps = apiSection.turns
            }

            r.sections.push(section);
        })
    }

    // sections – for sharing display
    if(route.mode === 'SHARING') {
        r.sections = []

        route.sections.forEach((apiSection, apiSectionIndex) => {
            let section = { type: apiSection.type };
            const sharingModeData = SHARING_MEANS_OF_TRANSPORT.find(item => item.value === route.sharingVehicle)

            if(apiSection.lineColor) {
                section.color = apiSection.lineColor ?? '#929292'
            }

            if(section.type === 'Drive') {
                section.name = sharingModeData.label
                section.driveLabel = sharingModeData.label //'Autofahrt'
                // if(section.lineType === 'Bike') section.driveLabel = 'Radweg'
                // if(section.lineType === 'Scooter') section.driveLabel = 'Scooterfahrt'
                if(sharingModeData.iconType){
                    section.lineType = sharingModeData.iconType;
                    section.driveType = sharingModeData.iconType
                }
                if(sharingModeData.image) {
                    section.image = sharingModeData.image
                }
            }

            if(options.detailedSections) {
                section.departureStation = apiSection.departure && apiSection.departure.station ? apiSection.departure.station.name : false;
                section.departureTime = false;
                if(apiSection.departure && apiSection.departure.time) {
                    section.departureTime = getTimeFromTimestamp(apiSection.departure.time.plannedTime)
                }
                section.dotted = isSectionDotted(apiSection, route);
                section.distance = formatDistance(apiSection.distance);
                section.duration = formatDuration(apiSection.duration);
                if(apiSection.turns) {
                    section.steps = apiSection.turns
                }
                if(section.type === 'Walk' && apiSectionIndex === 0 && store.getters.routeWaypoints.length) {
                    const label = store.getters.routeWaypoints[0].label;
                    section.departureStation = label.includes(',') ? label.split(',')[0] : label;
                }
                if(section.type === 'Drive') {
                    if(apiSection.departure && apiSection.departure.station && apiSection.departure.station.sharingInformation) {
                        const sharingInformation = apiSection.departure.station.sharingInformation
                        section.sharingInformation = {}
                        if(sharingInformation.vehicleDetails) section.sharingInformation.vehicleInfo = sharingInformation.vehicleDetails
                        if(sharingInformation.batteryLevel) section.sharingInformation.batteryLevel = sharingInformation.batteryLevel
                        if(sharingInformation.costs) section.sharingInformation.price = sharingInformation.costs
                        
                        if(Object.keys(section.sharingInformation).length === 0) section.sharingInformation = false

                        section.sharingWebsite = sharingInformation.links && sharingInformation.links.web ? sharingInformation.links.web : false
                        section.sharingDeepLink = sharingInformation.links && sharingInformation.links.deepLink ? sharingInformation.links.deepLink : false
                    }
                }
            }

            r.sections.push(section);
        })
    }

    // departure time (when arrival time is selected)
    if(route.metadata.time.departure.plannedTime && route.mode !== 'PUBLIC') {
        let departureTime = getTimeFromTimestamp(route.metadata.time.departure.plannedTime);
        let departureDate = getDateFromTimestamp(route.metadata.time.departure.plannedTime);
        let currentDate = getDateFromTimestamp(new Date().toISOString());
        if(currentDate !== departureDate) r.departureDate = departureDate;
        r.departureTime = departureTime;
    }

    // departure and arrival times
    if(HAS_PUBLIC_TRANSPORT.includes(route.mode) && route.metadata.time && route.metadata.time.arrival && route.metadata.time.departure) {
        let departure = getTimeFromTimestamp(route.metadata.time.departure.plannedTime);
        let arrival = getTimeFromTimestamp(route.metadata.time.arrival.plannedTime);
        r.departureArrival = `${departure} - ${arrival}`;
        r.departureDate = getDateFromTimestamp(route.metadata.time.departure.plannedTime);
        r.departure = departure;
        r.arrival = arrival;
    }
    // date is needed for bike / park & ride
    if(VEHICLE_PUBLIC_COMBINED.includes(route.mode)) {
        const routeDate = new Date(route.metadata.time.departure.plannedTime)
        r.departureDay = getDayName(routeDate)
    } 

    // first or last connection
    if(route.mode === 'PUBLIC') {
        if(route.isFirstOfDay) r.isFirstOfDay = true
        if(route.isLastOfDay) r.isLastOfDay = true
    }

    // fares
    if(HAS_PUBLIC_TRANSPORT.includes(route.mode)) {
        let responseFare = route.metadata.fare && route.metadata.fare.fareSum ? route.metadata.fare.fareSum : false;
        if(!responseFare){
            responseFare = route.metadata.fare && route.metadata.fare.transportNetworks ? route.metadata.fare.transportNetworks[0].entries[0].amount : false;
        }
        r.fare = responseFare ? formatPrice(responseFare) : false;
        r.showFareText = VEHICLE_PUBLIC_COMBINED.includes(route.mode)
    }

    // transitions
    if(HAS_PUBLIC_TRANSPORT.includes(route.mode) && route.metadata.transitions) {
        r.transitions = route.metadata.transitions; 
    }

    // warning
    if(
        route.operationMessages && route.operationMessages.messages && route.operationMessages.messages.length ||
        route.sections.some(section => section.operationMessages && section.operationMessages.messages && section.operationMessages.messages.length)
    ) {
        r.warning = true;
    }

    // gasprice
    const gasConsumptionSettings = store.getters.gasConsumptionSettings[route.mode.toLowerCase()]
    if(gasConsumptionSettings?.gasConsumption) {
        r.gasPrice = calculateGasPrice(parseFloat(gasConsumptionSettings.gasConsumption), parseFloat(gasConsumptionSettings.gasPrice), route.metadata?.metrics?.drivingDistance || route.metadata.distance)
        r.fullGasConsumption = calculateGasConsumption(parseFloat(gasConsumptionSettings.gasConsumption), route.metadata?.metrics?.drivingDistance || route.metadata.distance)
    }
    
    // eco commented out for now
    // if(route.metadata?.eco?.saving) {
    //     r.ecoSavings = route.metadata.eco.saving
    // }

    // additional message
    // r.additionalMessage = ''; TODO: add additional message displayed as a last row of result

    return r
}

export function getDetailedRouteData(route, options = false) {
    let r = getListResultRouteData(route, options);

    if(HAS_PUBLIC_TRANSPORT.includes(r.mode) && r.sections && r.sections.length){
        const lastSection = route.sections[route.sections.length - 1];
        r.endSection = {
            departureTime: lastSection.arrival && lastSection.arrival.time ? getTimeFromTimestamp(lastSection.arrival.time.plannedTime) : false,
            departureStation: lastSection.arrival && lastSection.arrival.station ? lastSection.arrival.station.name : false,
        }
        if(lastSection.type === 'Ride' && lastSection.arrival && lastSection.arrival.station && (lastSection.arrival.station.platform || lastSection.arrival.station.plannedPlatform)) {
            r.endSection.arrivalPlatform = {
                platform: lastSection.arrival.station.platform || lastSection.arrival.station.plannedPlatform,
                platformChanged: lastSection.arrival.station.platform && lastSection.arrival.station.plannedPlatform && lastSection.arrival.station.platform !== lastSection.arrival.station.plannedPlatform
            }
        }
        if(lastSection.type === 'Ride' && lastSection.arrival && lastSection.arrival.time && lastSection.arrival.time.realTime) {
            const timeDifference = new Date(lastSection.arrival.time.realTime) - new Date(lastSection.arrival.time.plannedTime);
            const timeDifferenceMinutes = Math.floor((timeDifference/1000/60) << 0);
            r.endSection.realDepartureTime = getTimeFromTimestamp(lastSection.arrival.time.realTime)
            r.endSection.delayDeparture = timeDifferenceMinutes
            r.endSection.delayDepartureFormatted = `${timeDifferenceMinutes < 0 ? '-' : '+'} ${timeDifferenceMinutes < 0 ? timeDifferenceMinutes * -1 : timeDifferenceMinutes}`
        }
        if(lastSection.type === 'Walk' && store.getters.routeWaypoints.length > 1) {
            const lastWaypointIndex = store.getters.routeWaypoints.length - 1
            const label = store.getters.routeWaypoints[lastWaypointIndex].label;
            r.endSection.departureStation = label.includes(',') ? label.split(',')[0] : label;
        }
    }

    if(r.mode === 'SHARING') {
        const lastSection = route.sections[route.sections.length - 1];
        r.endSection = {
            departureTime: lastSection.arrival && lastSection.arrival.time ? getTimeFromTimestamp(lastSection.arrival.time.plannedTime) : false,
            departureStation: lastSection.arrival && lastSection.arrival.station ? lastSection.arrival.station.name : false,
        }
        if(lastSection.type === 'Walk' && store.getters.routeWaypoints.length > 1) {
            const lastWaypointIndex = store.getters.routeWaypoints.length - 1
            const label = store.getters.routeWaypoints[lastWaypointIndex].label;
            r.endSection.departureStation = label.includes(',') ? label.split(',')[0] : label;
        }
    }

    // interim stop distances
    if(!NO_INTERMEDIATE_STOP_TRANSPORT_MODES.includes(route.mode)) {
        r.interimDistances = []
        route.sections.forEach(section => {
            r.interimDistances.push(formatDistanceToKm(section.distance))
        })
    }

    r.steps = getRouteSteps(route)
    
    return r;
}

export function getDetailedSectionsData(route) {
    let options = {
        detailedSections: true,
    }
    let r = getDetailedRouteData(route, options);

    return r;
}

function getRouteSteps(route) {
    let steps = []
    route.sections.forEach(section => {
        if(section.turns) {
            const turns = [...section.turns]
            turns.forEach(turn => {
                if(turn.polyLineIndex !== undefined && turn.polyLineIndex !== false && section.polyline && section.polyline[turn.polyLineIndex]) {
                    turn.coordinates = section.polyline[turn.polyLineIndex]
                }
            })
            steps = [...steps, ...turns]
        }
    });
    return steps.length > 0 ? steps : false
}

function getTimeFromTimestamp(timestamp) {
    return formatTimeFromTimestamp(timestamp);
}

function getDateFromTimestamp(timestamp) {
    return formatDateFromTimestamp(timestamp)
}

export function getDetailRoute() {
    const detailRouteId = store.getters.detailRouteId;
    if(!detailRouteId) return false;
    return getRouteById(detailRouteId);
}

export function getRouteById(searchId) {
    const transportModes = store.getters.transportModes;
    for(const modeKey in transportModes) {
        if(!transportModes[modeKey].routes) return false;
        let searched = transportModes[modeKey].routes.find(route => {
            return route.id === searchId;
        });
        if(searched) return searched;
    }
    return false;
}

export function getOverallPolyline(polylines) {
    if(!polylines || !polylines.length) {
        return false;
    } else if (polylines.length === 1) {
        return polylines[0].polyline;
    } else {
        let overallArray = [];
        polylines.forEach(polyline => {
            overallArray.push(polyline.polyline.replace('LINESTRING(', '').replace(')', ''));
        })
        let overallString = `LINESTRING(${overallArray.toString()})`;
        return overallString;
    }
}

export function prepareSavedRoutes(routes) {
    let prepared = routes.map(route => {
        if(route.id) {
            let item = {
                name: route.name,
                id: route.id,
                mode: route.meansOfTransport ? route.meansOfTransport[0] : 'CAR',
                waypoints: [],
                completeRoute: route
            }

            if(route.origin) {
                item.waypoints.push(prepareSavedRoute(route.origin)); 
            }
            if(route.vias && route.vias.length) {
                route.vias.forEach(via => {
                    item.waypoints.push(prepareSavedRoute(via)); 
                })
            }
            if(route.destination) {
                item.waypoints.push(prepareSavedRoute(route.destination)); 
            }

            return item;
        }
    });
    return prepared;
}

function prepareSavedRoute(route) {
    let name = route.name && route.name.indexOf(',') > 0 ? route.name.split(',')[0] : '';
    let prepared = {
        name: name,
        label: route.name,
        id: route.id,
        lon: route.geoLocation ? route.geoLocation.lon : false,
        lat: route.geoLocation ? route.geoLocation.lat : false,
    }
    return prepared;
}

export function getRouteCountryInfo(route) {
    const countryInfos = store.getters.routeCountryInfos[route.mode]
    const routeCountries = route.metadata.countries && route.metadata.countries.length ? route.metadata.countries : false
    const infos = []

    if(routeCountries && countryInfos) {
        routeCountries.forEach(country => {
            const info = {
                countryCode: country.countryCode,
                name: country.name,
                image: `${process.env.BASE_URL}/images/flags/${country.countryCode.toLowerCase()}.png`,
                data: false
            }
            const countrySpecificInfo = countryInfos.find(i => i.countryCode === country.countryCode)
            if(countrySpecificInfo) {
                const data = countrySpecificInfo.structured
                info.data = formatCountryInfo(data)
                info.name = data.general[0] && data.general[0].countryName ? data.general[0].countryName.value : ''
            }
            infos.push(info)
        })
    }
    return infos
}

export function getCountryNamesFromCountryInfo(route) {
    const countryInfos = store.getters.routeCountryInfos[route.mode]
    const routeCountries = route.metadata.countries && route.metadata.countries.length ? route.metadata.countries : false
    const countries = []

    if(routeCountries && countryInfos) {
        routeCountries.forEach(country => {
            const countrySpecificInfo = countryInfos.find(i => i.countryCode === country.countryCode)
            const countryData = {
                countryCode: country.countryCode,
                name: country.name,
            }
            if(countrySpecificInfo) {
                const data = countrySpecificInfo.structured
                countryData.name = data.general[0] && data.general[0].countryName ? data.general[0].countryName.value : ''
            }
            countries.push(countryData)
        })
    }
    return countries
}

function isSectionDotted(section, route = false) {
    if(route) {
        if(route.mode === 'BIKE') return true
        if(route.mode === 'BIKE_AND_RIDE' && section.type === 'Drive') return true
    }
    return section.type === 'Walk';
}

export function prepareRouteFavouriteOptions(routeParams) {
    const availableSettings = ['exclude', 'publicTransportationParameters', 'bikeParameters', 'startTime', 'arrivalTime', 'mode']
    let options = []
    for(const r in routeParams) {
        const param = routeParams[r]
        if(availableSettings.includes(r)) {
            if(r === 'exclude') {
                for(const p in param) {
                    let name = `exclude${capitalize(p)}`
                    let value
                    if(p === 'maxTunnelLength') {
                        name = 'excludeTunnels'
                        value = 'true'
                        options.push({
                            name: 'tunnelLength',
                            value: param[p].toString()
                        })
                    } else if(p === 'countries') {
                        value = param[p] && param[p].length > 0 ? 'true' : 'false'
                        if(value) {
                            options.push({
                                name: 'excludeCountryList',
                                value: param[p].join(',')
                            })
                        }
                    } else {
                        value = param[p].toString()
                    }

                    if(value === 'true') {
                        options.push({
                            name: name,
                            value: value
                        })
                    }
                }
            } else if(r === 'mode') {
                options.push({
                    name: 'routeMode',
                    value: param
                })
            } else if(r === 'publicTransportationParameters') {
                if(param.meansOfTransport) {
                    let uniqueTransportModes = [...new Set(param.meansOfTransport)]
                    options.push({
                        name: 'meansOfTransport',
                        value: uniqueTransportModes.join(',')
                    })
                }
                if(param.avoidBarriers) {
                    options.push({
                        name: 'avoidBarriers',
                        value: param.avoidBarriers.join(',')
                    })
                } 
                if(param.maxChanges) {
                    options.push({
                        name: 'maxChanges',
                        value: param.maxChanges.toString()
                    })
                }
                if(param.bikeCarriage) {
                    options.push({
                        name: 'bikeCarriage',
                        value: param.bikeCarriage
                    })
                }
            } else if(r === 'bikeParameters') {
                if(param.avoidInclines) {
                    options.push({
                        name: 'avoidInclines',
                        value: 'true'
                    })
                }
                options.push({
                    name: 'preferCyclingInfrastructure',
                    value: param.preferCyclingInfrastructure ? 'true' : 'false'
                })
            } else if(r === 'startTime' || r === 'arrivalTime') {
                const now = new Date()
                if(param > now) {
                    options.push({
                        name: r,
                        value: param
                    })
                }
            } else {
                options.push({
                    name: r,
                    value: param
                })
            }
        }
    }
    return options
}

export function unprepareRouteFavouriteOptions(options, transportMode) {
    const settings = JSON.parse(JSON.stringify(routeSettingsDefaults))
    const transportModeKey = transportMode.toLowerCase()
    const time = { isNow: true } // default if routeParameters does not have startTime / arrivalTime

    options.forEach(option => {
        if(option.name === 'meansOfTransport' || option.name === 'avoidBarriers') {
            let options = option.value.replaceAll(' ', '')
            options = options.split(',')
            options = options.filter(option => option !== '')
            settings[transportModeKey][option.name] = options
            // filter out the extra transport params from public transport to show the red dot correctly
            if(option.name === 'meansOfTransport') {
                const publicSubModes = PUBLIC_MEANS_OF_TRANSPORT_SUB.map(publicMode => publicMode.value)
                settings[transportModeKey][option.name] = options.filter(option => {
                    return !publicSubModes.includes(option)
                })
            }
        } else if(option.name.includes('exclude')) {
            if(option.name === 'excludeCountryList') {
                if(options.find(o => o.name === 'excludeCountries').value === 'true') {
                    settings[transportModeKey].avoidCountries = option.value.split(',')
                }
            } else if(option.name === 'excludeTunnels') {
                settings[transportModeKey].avoidTunnels = option.value === 'true' ? options.find(o => o.name === 'tunnelLength').value  : false
            } else {
                const key = option.name.replace('exclude', '').toLowerCase()
                let value = option.value
                if(value === 'false') value = false
                if(value === 'true') value = true
                settings[transportModeKey].avoid[key] = value
            }
        } else if(option.name === 'routeMode') {
            if(settings[transportModeKey]?.travelMode !== undefined) { // only allow setting the mode for items that have it
                settings[transportModeKey].travelMode = option.value
            }
        } else if(option.name === 'avoidInclines' || option.name === 'preferCyclingInfrastructure') {
            settings[transportModeKey].bikeParameters[option.name] = option.value === 'true' ? true : false
        } else if(option.name !== 'startTime' && option.name !== 'arrivalTime') {
            let value = option.value
            if(value === 'false') value = false
            if(value === 'true') value = true
            settings[transportModeKey][option.name] = value
        }

        if(option.name === 'startTime' || option.name === 'arrivalTime') {
            const now = new Date()
            const routeDate = new Date(option.value)
            if(routeDate > now) {
                time.date = new Date(option.value)
                time.time = formatTimeFromTimestamp(option.value)
                time.mode = option.name === 'arrivalTime' ? 'arrival' :'departure'
                time.isNow = false
            }
        }
    })
    return { settings, time }
}

export function formatRouteSteps(steps, showFinish = true) {
    if(!steps) return false
    const formattedSteps = []
    for (const index in steps) {
        const step = steps[index]
        if(step.distance >= 0) {
            let distance = formatDistanceToKm(step.distance)
            let relativeDistance = formatDistance(step.relativeDistance)
            if(step.relativeDistance === 0 && step.type === 'FINISH') {
                relativeDistance = 'Ziel erreicht'
            } else if(parseInt(index) === 0) {
                relativeDistance = 'Start'
                step.type = 'START'
            } else if(step.relativeDistance === 0 && steps.length - 1 !== index) {
                relativeDistance = ''
            } else {
                relativeDistance = formatDistance(step.relativeDistance);
            }
            const formattedStep = {
                relativeDistance: relativeDistance,
                distance: distance,
                text: step.text,
                icon: getStepIcon(step.type),
                notes: getStepNotes(step.notes),
            }
            if(step.type === 'FINISH' && !showFinish) continue;
            formattedSteps.push(formattedStep)
        }
    }

    return formattedSteps.length > 0 ? formattedSteps : false;
}


function getStepIcon(type) {
    let icon = false
    switch(type) {
        case 'STRAIGHT':
            icon = 'icon-straight'
            break
        case 'START':
            icon = 'icon-start'
            break
        case 'FINISH':
            icon = 'icon-goal'
            break
        case 'TURN_LEFT':
            icon = 'icon-left'
            break
        case 'TURN_RIGHT':
            icon = 'icon-right'
            break
        case 'KEEP_RIGHT':
        case 'ON_RAMP_RIGHT':
        case 'TURN_HALF_RIGHT':
            icon = 'icon-half-right'
            break
        case 'KEEP_LEFT':
        case 'ON_RAMP_LEFT':
        case 'TURN_HALF_LEFT':
            icon = 'icon-half-left'
            break
        case 'EXIT_RIGHT':
            icon = 'icon-exit'
            break
        case 'ENTER_ROUND_ABOUT':
        case 'LEAVE_ROUND_ABOUT':
            icon = 'icon-roundabout'
            break
        case 'USE_FERRY':
        case 'EXIT_MOTORAIL':
            icon = 'icon-transport'
            break
        case 'LEFT_U_TURN':
            icon = 'icon-u-turn'
            break
        default:
            // icon = 'icon-straight'
            icon = false
            
    }

    if(icon) return `${process.env.BASE_URL}/images/route-step-icons/${icon}.svg`
    return false;
}

function getStepNotes(notes) {
    if(notes && notes.length > 0) {
       for(const note of notes) {
            if(note && note.bike) {
                let parseNotes = JSON.parse(JSON.stringify(note.bike));
                return parseNotes.infrastructure
            }
        }
    }
    return false
}

export function formatRouteTimeForPoi(routeTime) {
    const splitTime = routeTime.time.split(':')
    let startTime = new Date(routeTime.date)
    startTime.setHours(splitTime[0], splitTime[1])
    let endTime = new Date(routeTime.date)
    endTime.setHours(parseInt(splitTime[0]) + 2, splitTime[1]);
    return {
        futureOnly: false,
        periodFrom: startTime,
        periodTo: endTime,
    }
}